import type { ElectronAPI } from '../../types/electron';
import { isFileSystemAccessSupported } from './fileSystemAccess';
import {
  readFilesFromDirectoryHandle,
  createFileListFromArray,
} from './fileReadingUtils';
import { scanProjectsElectron } from './projectScanningUtils';
import {
  detectProjectsFromFiles,
  detectStemsProject,
  STEMS_AUDIO_EXTENSIONS,
  type DetectedProject,
} from './projectDetection';
import { getDAWTypeFromExtension } from './dawUtils';

export interface FolderSelectionResult {
  rootDirHandle: FileSystemDirectoryHandle | null;
  rootPath: string | undefined;
  fileList: FileList;
  detectedProjects: DetectedProject[];
}

export interface FolderSelectionCallbacks {
  onFileScanned?: (filePath: string) => void;
  onError?: (message: string) => void;
}

/**
 * Select folder and detect projects using Electron API
 */
async function selectFolderElectron(
  electronAPI: ElectronAPI,
  callbacks: FolderSelectionCallbacks
): Promise<FolderSelectionResult | null> {
  try {
    const selectedPath = await electronAPI.selectFolder();
    if (!selectedPath) {
      return null;
    }

    // Create a mock handle for consistency
    const rootDirHandle = {
      name: selectedPath.split(/[/\\]/).pop() || 'Project',
      kind: 'directory' as const,
    } as FileSystemDirectoryHandle;

    // Read files from the directory using Electron API (metadata-only for fast detection)
    const { files: fileList, detectedProjects: detected } =
      await scanProjectsElectron(electronAPI, selectedPath, {
        readContent: false, // metadata-only for fast detection
        onFileScanned: callbacks.onFileScanned,
      });

    if (fileList.length === 0) {
      callbacks.onError?.('Selected folder is empty or contains no files.');
      return null;
    }

    if (detected.length === 0) {
      callbacks.onError?.(
        'No DAW project files detected. Please select a project folder or a parent folder that contains DAW project files (.als, .logicx, .ptf, .cpr, .flp, etc.).'
      );
      return null;
    }

    return {
      rootDirHandle,
      rootPath: selectedPath,
      fileList,
      detectedProjects: detected,
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return null;
    }
    callbacks.onError?.('Failed to select folder. Please try again.');
    return null;
  }
}

/** Scan a known local folder without reopening the folder picker. */
export async function detectProjectsAtElectronPath(
  electronAPI: ElectronAPI,
  selectedPath: string,
  savedProjectFilePath?: string,
  callbacks: FolderSelectionCallbacks = {}
): Promise<FolderSelectionResult | null> {
  try {
    const rootDirHandle = {
      name: selectedPath.split(/[/\\]/).pop() || 'Project',
      kind: 'directory' as const,
    } as FileSystemDirectoryHandle;
    const { files: fileList, detectedProjects: scannedProjects } =
      await scanProjectsElectron(electronAPI, selectedPath, {
        readContent: false,
        onFileScanned: callbacks.onFileScanned,
      });
    let detectedProjects = scannedProjects;

    // Logic and GarageBand projects are macOS package directories. Their
    // extension is on the directory rather than an individual scanned file.
    if (detectedProjects.length === 0 && savedProjectFilePath) {
      const packageName = savedProjectFilePath.split(/[/\\]/).pop() || '';
      const extension = packageName
        .slice(packageName.lastIndexOf('.'))
        .toLowerCase();
      if (extension === '.logicx' || extension === '.band') {
        const packageFiles = Array.from(fileList).filter((file) =>
          file.webkitRelativePath.startsWith(`${packageName}/`)
        );
        if (packageFiles.length > 0) {
          detectedProjects = [
            {
              folderPath: packageName,
              projectName: packageName.slice(0, -extension.length),
              dawType: getDAWTypeFromExtension(extension),
              fileCount: packageFiles.length,
              totalSize: packageFiles.reduce(
                (total, file) => total + file.size,
                0
              ),
              dawFiles: [packageName],
              rendersFolder: null,
            },
          ];
        }
      }
    }

    if (fileList.length === 0 || detectedProjects.length === 0) {
      callbacks.onError?.(
        'No DAW project files were found in the saved project folder.'
      );
      return null;
    }

    return {
      rootDirHandle,
      rootPath: selectedPath,
      fileList,
      detectedProjects,
    };
  } catch {
    callbacks.onError?.('Failed to scan the saved project. Please try again.');
    return null;
  }
}

/**
 * Select folder and detect projects using File System Access API (web browsers)
 */
async function selectFolderWeb(
  callbacks: FolderSelectionCallbacks
): Promise<FolderSelectionResult | null> {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const rootDirHandle = await window.showDirectoryPicker({
      mode: 'readwrite',
    });

    if (!rootDirHandle) {
      return null;
    }

    // Read all files from the directory
    const files = await readFilesFromDirectoryHandle(rootDirHandle);
    const fileList = createFileListFromArray(files);

    if (fileList.length === 0) {
      callbacks.onError?.('Selected folder is empty or contains no files.');
      return null;
    }

    const detected = detectProjectsFromFiles(fileList, {
      rootFolderName: rootDirHandle.name,
    });
    if (detected.length === 0) {
      callbacks.onError?.(
        'No DAW project files detected. Please select a project folder or a parent folder that contains DAW project files (.als, .logicx, .ptf, .cpr, .flp, etc.).'
      );
      return null;
    }

    return {
      rootDirHandle,
      rootPath: undefined, // No path available in web browsers
      fileList,
      detectedProjects: detected,
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // User cancelled
      return null;
    }
    callbacks.onError?.('Failed to select folder. Please try again.');
    return null;
  }
}

/**
 * Select folder using fallback file input (for browsers without File System Access API)
 */
function selectFolderFallback(
  callbacks: FolderSelectionCallbacks
): Promise<FolderSelectionResult | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const detected = detectProjectsFromFiles(files, {
          rootFolderName: 'Selected Folder',
        });
        if (detected.length > 0) {
          resolve({
            rootDirHandle: null,
            rootPath: undefined,
            fileList: files,
            detectedProjects: detected,
          });
        } else {
          callbacks.onError?.(
            'No DAW project files detected. Please select a project folder or a parent folder that contains DAW project files (.als, .logicx, .ptf, .cpr, .flp, etc.).'
          );
          resolve(null);
        }
      } else {
        resolve(null);
      }
    };
    input.click();
  });
}

/**
 * Select a folder and detect projects within it
 * Handles Electron, File System Access API, and fallback file input
 */
export async function selectFolderAndDetectProjects(
  isElectron: boolean,
  electronAPI: ElectronAPI | null | undefined,
  callbacks: FolderSelectionCallbacks = {}
): Promise<FolderSelectionResult | null> {
  // Check Electron first (prioritize Electron over web APIs)
  if (isElectron && electronAPI?.selectFolder) {
    return selectFolderElectron(electronAPI, callbacks);
  }

  // For web browsers, use File System Access API
  if (isFileSystemAccessSupported() && typeof window !== 'undefined') {
    return selectFolderWeb(callbacks);
  }

  // Fallback to file input for browsers that don't support File System Access API
  return selectFolderFallback(callbacks);
}

/**
 * Select a folder containing exported stems (audio files only).
 * Non-audio files are excluded. One folder = one project.
 * Handles Electron, File System Access API, and fallback file input.
 */
export async function selectFolderForStems(
  isElectron: boolean,
  electronAPI: ElectronAPI | null | undefined,
  callbacks: FolderSelectionCallbacks = {}
): Promise<FolderSelectionResult | null> {
  if (isElectron && electronAPI?.selectFolder) {
    return selectStemsFolderElectron(electronAPI, callbacks);
  }
  if (isFileSystemAccessSupported() && typeof window !== 'undefined') {
    return selectStemsFolderWeb(callbacks);
  }
  return selectStemsFolderFallback(callbacks);
}

/**
 * Filter a FileList/array to audio-only files and build a stems FileList.
 */
function filterToAudioFiles(files: File[]): File[] {
  return files.filter((f) => {
    const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
    return STEMS_AUDIO_EXTENSIONS.has(ext);
  });
}

function buildStemsFileList(audioFiles: File[]): FileList {
  const dt = new DataTransfer();
  audioFiles.forEach((f) => dt.items.add(f));
  return dt.files;
}

async function selectStemsFolderElectron(
  electronAPI: ElectronAPI,
  callbacks: FolderSelectionCallbacks
): Promise<FolderSelectionResult | null> {
  try {
    const selectedPath = await electronAPI.selectFolder();
    if (!selectedPath) return null;

    const rootDirHandle = {
      name: selectedPath.split(/[/\\]/).pop() || 'Stems',
      kind: 'directory' as const,
    } as FileSystemDirectoryHandle;

    const folderName = selectedPath.split(/[/\\]/).pop() || 'Stems';
    const { files: allFileList } = await scanProjectsElectron(
      electronAPI,
      selectedPath,
      {
        readContent: false,
        onFileScanned: callbacks.onFileScanned,
      }
    );

    const allFiles = Array.from(allFileList);
    const audioFiles = filterToAudioFiles(allFiles);

    if (audioFiles.length === 0) {
      callbacks.onError?.(
        'No audio files found in the selected folder. Please select a folder containing audio files (.wav, .mp3, .aiff, .flac, etc.).'
      );
      return null;
    }

    return {
      rootDirHandle,
      rootPath: selectedPath,
      fileList: buildStemsFileList(audioFiles),
      detectedProjects: [detectStemsProject(folderName, audioFiles)],
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return null;
    callbacks.onError?.('Failed to select folder. Please try again.');
    return null;
  }
}

async function selectStemsFolderWeb(
  callbacks: FolderSelectionCallbacks
): Promise<FolderSelectionResult | null> {
  try {
    const rootDirHandle = await window.showDirectoryPicker({
      mode: 'readwrite',
    });
    if (!rootDirHandle) return null;

    const allFiles = await readFilesFromDirectoryHandle(rootDirHandle);
    const audioFiles = filterToAudioFiles(allFiles);

    if (audioFiles.length === 0) {
      callbacks.onError?.(
        'No audio files found in the selected folder. Please select a folder containing audio files (.wav, .mp3, .aiff, .flac, etc.).'
      );
      return null;
    }

    return {
      rootDirHandle,
      rootPath: undefined,
      fileList: buildStemsFileList(audioFiles),
      detectedProjects: [detectStemsProject(rootDirHandle.name, audioFiles)],
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return null;
    callbacks.onError?.('Failed to select folder. Please try again.');
    return null;
  }
}

function selectStemsFolderFallback(
  callbacks: FolderSelectionCallbacks
): Promise<FolderSelectionResult | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || files.length === 0) {
        resolve(null);
        return;
      }

      const allFiles = Array.from(files);
      const audioFiles = filterToAudioFiles(allFiles);

      if (audioFiles.length === 0) {
        callbacks.onError?.(
          'No audio files found in the selected folder. Please select a folder containing audio files (.wav, .mp3, .aiff, .flac, etc.).'
        );
        resolve(null);
        return;
      }

      // Derive folder name from the first file's path
      const firstPath =
        (allFiles[0] as File & { webkitRelativePath?: string })
          .webkitRelativePath || '';
      const folderName = firstPath.split('/')[0] || 'Stems';

      resolve({
        rootDirHandle: null,
        rootPath: undefined,
        fileList: buildStemsFileList(audioFiles),
        detectedProjects: [detectStemsProject(folderName, audioFiles)],
      });
    };
    input.click();
  });
}
